import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { phase1Api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import PortalSidebar from "../components/PortalSidebar";
import PortalTopbar from "../components/PortalTopbar";
import TransporterServiceCard from "../components/TransporterServiceCard";

function statusTone(value) {
  const status = String(value || "").toUpperCase();
  if (["APPROVED", "ACTIVE", "ASSIGNED", "DELIVERED", "COMPLETED"].includes(status)) return "good";
  if (["PENDING", "REQUESTED", "QUOTED", "PICKED_UP", "IN_TRANSIT", "UNDER_REVIEW"].includes(status)) return "warn";
  if (["REJECTED", "SUSPENDED", "CANCELLED"].includes(status)) return "bad";
  return "neutral";
}

function formatStatus(value) {
  return String(value || "PENDING").replaceAll("_", " ");
}

function money(value, currency = "US$") {
  const amount = Number(value || 0);
  return `${currency || "US$"}${amount.toFixed(2)}`;
}

function Modal({ title, eyebrow, children, onClose, wide = false }) {
  return (
    <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
      <section className={`tazy-modal-card transporter-modal-card modal-card-pro action-modal-card ${wide ? "wide-modal" : ""}`}>
        <header className="tazy-modal-header">
          <div><span>{eyebrow}</span><h3>{title}</h3></div>
          <button type="button" onClick={onClose}>×</button>
        </header>
        <div className="tazy-modal-body">{children}</div>
      </section>
    </div>
  );
}

const serviceTypes = [
  ["LOCAL_DELIVERY", "Local delivery"],
  ["INTERCITY", "Intercity"],
  ["HEAVY_GOODS", "Heavy goods"],
  ["COLD_CHAIN", "Cold chain"],
  ["SAME_DAY", "Same day"],
  ["OTHER", "Other"],
];
const documentTypes = [["NATIONAL_ID", "National ID"], ["DRIVER_LICENCE", "Driver licence"], ["VEHICLE_REGISTRATION", "Vehicle registration"], ["OTHER", "Other"]];

function DeliveryStageRail({ status }) {
  const steps = [
    ["REQUESTED", "Request"],
    ["QUOTED", "Quote"],
    ["ASSIGNED", "Accepted"],
    ["PICKED_UP", "Picked up"],
    ["IN_TRANSIT", "Transit"],
    ["DELIVERED", "Delivered"],
  ];
  const currentIndex = Math.max(0, steps.findIndex(([code]) => code === status));
  return (
    <div className="delivery-stage-rail">
      {steps.map(([code, label], index) => (
        <span key={code} className={`${index <= currentIndex ? "done" : ""} ${code === status ? "active" : ""}`}>{label}</span>
      ))}
    </div>
  );
}

export default function TransporterWorkspace() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [couriers, setCouriers] = useState([]);
  const [services, setServices] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [statusModal, setStatusModal] = useState(null);
  const [serviceModal, setServiceModal] = useState(false);
  const [profileModal, setProfileModal] = useState(false);
  const [docModal, setDocModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: "QUOTED", quoted_amount: "", scheduled_date: "" });
  const [serviceForm, setServiceForm] = useState({ courier: "", service_name: "", service_type: "LOCAL_DELIVERY", base_price: "0", price_notes: "", service_areas_text: "", service_image: null, image_url: "" });
  const [profileForm, setProfileForm] = useState({ transporter_name: "", vehicle_type: "VAN", vehicle_registration: "", base_city: "", base_province: "", service_area_summary: "", contact_phone: "" });
  const [docForm, setDocForm] = useState({ document_type: "NATIONAL_ID", title: "National ID", document_number: "", file: null });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const isCourier = user?.account_type === "COURIER";
  const isAdmin = ["SUPER_ADMIN", "ADMIN", "OPERATIONS", "SUPPORT"].includes(user?.account_type) || user?.is_superuser || user?.is_platform_staff;
  const myCourier = useMemo(() => couriers.find((item) => item.user === user?.id) || (isAdmin ? couriers[0] : null), [couriers, user, isAdmin]);
  const myServices = useMemo(() => myCourier ? services.filter((item) => String(item.courier) === String(myCourier.id)) : services, [services, myCourier]);
  const myDocs = useMemo(() => isCourier ? documents.filter((item) => item.owner === user?.id || item.owner_account_type === "COURIER") : documents, [documents, isCourier, user]);
  const visibleDeliveries = isCourier && myCourier ? deliveries.filter((item) => String(item.courier) === String(myCourier.id)) : deliveries;
  const newRequests = visibleDeliveries.filter((item) => item.status === "REQUESTED");
  const activeJobs = visibleDeliveries.filter((item) => !["REQUESTED", "DELIVERED", "CANCELLED"].includes(item.status));
  const completeJobs = visibleDeliveries.filter((item) => ["DELIVERED", "CANCELLED"].includes(item.status));

  function hydrateProfile(profile) {
    setProfileForm({
      transporter_name: profile?.transporter_name || user?.business_name || user?.display_name || "",
      vehicle_type: profile?.vehicle_type || "VAN",
      vehicle_registration: profile?.vehicle_registration || "",
      base_city: profile?.base_city || user?.city || "",
      base_province: profile?.base_province || user?.province || "",
      service_area_summary: profile?.service_area_summary || "",
      contact_phone: profile?.contact_phone || user?.phone || "",
    });
  }

  async function load() {
    setLoading(true); setError("");
    try {
      const [c, s, d, docs] = await Promise.all([phase1Api.couriers(), phase1Api.transporterServices(), phase1Api.deliveries(), phase1Api.documents()]);
      const courierRows = Array.isArray(c.data) ? c.data : [];
      setCouriers(courierRows); setServices(Array.isArray(s.data) ? s.data : []); setDeliveries(Array.isArray(d.data) ? d.data : []); setDocuments(Array.isArray(docs.data) ? docs.data : []);
      const mine = courierRows.find((item) => item.user === user?.id) || (isAdmin ? courierRows[0] : null);
      if (mine) { setServiceForm((prev) => ({ ...prev, courier: mine.id })); hydrateProfile(mine); } else { hydrateProfile(null); }
    } catch {
      setError("Transporter workspace could not be loaded right now.");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function saveDeliveryStatus(event) {
    event.preventDefault();
    if (!statusModal?.id) return;
    await moveDelivery(statusModal, statusForm.status, { quoted_amount: statusForm.quoted_amount, scheduled_date: statusForm.scheduled_date });
    setStatusModal(null);
  }

  async function moveDelivery(item, status, extra = {}) {
    setBusy(`${item.id}-${status}`); setMessage(""); setError("");
    try {
      await phase1Api.deliveryStatus(item.id, { status, ...extra });
      const copy = status === "PICKED_UP" ? "Pickup confirmed. Order tracking is now moving into transit." : `Delivery ${item.code} updated to ${formatStatus(status)}.`;
      setMessage(copy);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Delivery status could not be updated.");
    } finally {
      setBusy("");
    }
  }

  async function checkSellerPayment(item) {
    const payment = item.latest_payment;
    if (!payment?.id) return;
    setBusy(`${payment.id}-PAYMENT-CHECK`); setMessage(""); setError("");
    try {
      const response = await phase1Api.checkPaynowPayment(payment.id);
      if (response.data?.status === "CONFIRMED") {
        setMessage(`Seller payment ${response.data?.code || payment.code} is confirmed. You can now confirm pickup.`);
      } else {
        setMessage(`Seller payment ${payment.code || "request"} is still awaiting approval or network confirmation.`);
      }
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Seller payment status could not be checked.");
    } finally {
      setBusy("");
    }
  }

  function openQuote(item) {
    setStatusModal(item);
    setStatusForm({ status: "QUOTED", quoted_amount: item.quoted_amount || "", scheduled_date: item.scheduled_date || "" });
  }

  async function saveService(event) {
    event.preventDefault(); setMessage(""); setError("");
    const courierId = serviceForm.courier || myCourier?.id;
    if (!courierId) return setError("Create your transporter profile before adding a delivery service.");
    try {
      const data = new FormData();
      data.append("courier", courierId);
      data.append("service_name", serviceForm.service_name);
      data.append("service_type", serviceForm.service_type);
      data.append("base_price", serviceForm.base_price || "0");
      data.append("price_notes", serviceForm.price_notes || "");
      data.append("service_areas", JSON.stringify(serviceForm.service_areas_text.split(",").map((item) => item.trim()).filter(Boolean)));
      data.append("image_url", serviceForm.image_url || "");
      data.append("is_active", "true");
      if (serviceForm.service_image) data.append("service_image", serviceForm.service_image);
      await phase1Api.createTransporterService(data);
      setMessage("Delivery service added. Sellers and businesses can now choose it during fulfilment once your transporter profile is approved.");
      setServiceModal(false);
      setServiceForm((prev) => ({ ...prev, service_name: "", price_notes: "", service_areas_text: "", service_image: null, image_url: "" }));
      await load();
    } catch (err) { setError(err?.response?.data?.detail || "Transporter service could not be saved."); }
  }

  async function saveProfile(event) {
    event.preventDefault(); setMessage(""); setError("");
    try {
      if (myCourier?.id) { await phase1Api.updateCourier(myCourier.id, profileForm); setMessage("Transporter profile updated."); }
      else { await phase1Api.createCourier({ user: user?.id, ...profileForm }); setMessage("Transporter profile created. Upload verification documents so admin can approve it."); }
      setProfileModal(false); await load();
    } catch (err) { setError(err?.response?.data?.detail || "Transporter profile could not be saved."); }
  }

  async function uploadDocument(event) {
    event.preventDefault(); setMessage(""); setError("");
    if (!docForm.file) return setError("Attach the document file before submitting it for review.");
    const data = new FormData();
    data.append("document_type", docForm.document_type); data.append("title", docForm.title || docForm.document_type); data.append("document_number", docForm.document_number); data.append("file", docForm.file);
    try { await phase1Api.createDocument(data); setMessage("Document uploaded for review."); setDocModal(false); setDocForm({ document_type: "NATIONAL_ID", title: "National ID", document_number: "", file: null }); await load(); }
    catch (err) { setError(err?.response?.data?.detail || "Document upload failed."); }
  }

  function JobCard({ item, mode }) {
    const current = item.status;
    return (
      <article className="delivery-deal-card">
        <div className="delivery-deal-head">
          <div><span className={`status-chip ${statusTone(current)}`}>{formatStatus(current)}</span><h3>{item.code} • {item.order_number}</h3></div>
          <div className="delivery-quote-summary"><span>Delivery quote</span><strong>{current === "REQUESTED" ? "Awaiting quote" : money(item.quoted_amount, item.order_currency || "US$")}</strong></div>
        </div>
        <div className="delivery-deal-grid">
          <div><span>Service</span><strong>{item.service_name || item.requested_service || "Delivery service"}</strong></div>
          <div><span>Seller</span><strong>{item.seller_name || "Seller"}</strong></div>
          <div><span>Buyer</span><strong>{item.buyer_name || "Buyer"}</strong></div>
          <div><span>Drop-off</span><strong>{item.dropoff_address || "Not supplied"}</strong></div>
          <div><span>Pickup</span><strong>{item.pickup_address || "Seller to confirm"}</strong></div>
          <div><span>Schedule</span><strong>{item.scheduled_date || "Not scheduled"}</strong></div>
          <div><span>Seller payment</span><strong>{formatStatus(item.payment_status || "UNPAID")}</strong></div>
        </div>
        {item.order_items_summary && <p className="delivery-items-summary">{item.order_items_summary}</p>}
        <DeliveryStageRail status={current} />
        <div className="delivery-deal-actions">
          {current === "REQUESTED" && <button type="button" onClick={() => openQuote(item)}>Send delivery quote</button>}
          {current === "QUOTED" && <>{["UNPAID", "FAILED"].includes(item.payment_status) ? <button type="button" className="ghost-action" onClick={() => openQuote(item)}>Update quote</button> : null}{item.payment_status === "PENDING" && item.latest_payment?.payment_method === "PAYNOW" ? <button type="button" disabled={busy === `${item.latest_payment.id}-PAYMENT-CHECK`} onClick={() => checkSellerPayment(item)}>{busy === `${item.latest_payment.id}-PAYMENT-CHECK` ? "Checking payment..." : "Check payment confirmation"}</button> : null}<span className="delivery-payment-wait">{item.payment_status === "PENDING" ? "Payment confirmation pending" : "Waiting for seller payment"}</span></>}
          {current === "ASSIGNED" && <button type="button" disabled={busy === `${item.id}-PICKED_UP`} onClick={() => moveDelivery(item, "PICKED_UP")}>Confirm pickup</button>}
          {current === "PICKED_UP" && <button type="button" disabled={busy === `${item.id}-IN_TRANSIT`} onClick={() => moveDelivery(item, "IN_TRANSIT")}>Start in transit</button>}
          {current === "IN_TRANSIT" && <button type="button" disabled={busy === `${item.id}-DELIVERED`} onClick={() => moveDelivery(item, "DELIVERED")}>Mark delivered</button>}
          {(current === "REQUESTED" || (current === "QUOTED" && item.payment_status !== "PENDING")) && <button type="button" className="danger-ghost" disabled={busy === `${item.id}-CANCELLED`} onClick={() => moveDelivery(item, "CANCELLED")}>Cancel</button>}
        </div>
      </article>
    );
  }

  return (
    <div className={`portal-shell-v2 refined-portal-shell ${collapsed ? "is-collapsed" : ""}`}>
      <PortalSidebar collapsed={collapsed} onToggle={() => setCollapsed((value) => !value)} />
      <main className="portal-main-v2 workspace-main transporter-workspace-main transporter-workspace-polished">
        <PortalTopbar user={user} onLogout={logout} />

        <section className="transporter-command-hero transporter-command-hero--clear">
          <div>
            <span>TRANSPORTER OPERATIONS</span>
            <h1>Service catalogue and delivery deals.</h1>
            <p>Publish delivery services, quote selected orders, then start pickup after the seller's delivery payment is confirmed.</p>
          </div>
          <div className="transporter-hero-actions">
            <button type="button" onClick={() => { hydrateProfile(myCourier); setProfileModal(true); }}>{myCourier ? "Edit profile" : "Create profile"}</button>
            <button type="button" onClick={() => setServiceModal(true)}>Add service</button>
            <button type="button" onClick={() => setDocModal(true)}>Upload document</button>
          </div>
        </section>

        {message && <div className="success-box workspace-alert">{message}</div>}
        {error && <div className="error-box workspace-alert">{error}</div>}

        {loading ? <div className="workspace-card">Loading transporter workspace...</div> : (
          <>
            <section className="transporter-readiness-grid transporter-readiness-grid--ops">
              <article className="workspace-card transporter-profile-summary-card">
                <div className="workspace-section-title"><span>PROFILE</span><h2>{myCourier?.transporter_name || "Transporter profile not completed"}</h2></div>
                <dl className="profile-summary-grid business-profile-metrics">
                  <div><dt>Status</dt><dd><span className={`status-chip ${statusTone(myCourier?.status)}`}>{myCourier?.status || "PENDING"}</span></dd></div>
                  <div><dt>Vehicle</dt><dd>{myCourier?.vehicle_type || "—"}</dd></div>
                  <div><dt>Base city</dt><dd>{myCourier?.base_city || "—"}</dd></div>
                  <div><dt>Platform contact</dt><dd>Managed through TAZY CART</dd></div>
                </dl>
                <p className="module-help-text">Complete your profile and documents before relying on marketplace delivery requests.</p>
              </article>

              <article className="workspace-card transporter-market-card">
                <div className="workspace-section-title"><span>OPERATIONS</span><h2>Delivery workload</h2></div>
                <div className="mini-metric-row"><div><strong>{myServices.length}</strong><span>Services</span></div><div><strong>{newRequests.length}</strong><span>New requests</span></div><div><strong>{activeJobs.length}</strong><span>Active jobs</span></div></div>
                <Link to="/transporters">View public transporter page</Link>
              </article>
            </section>

            <section className="workspace-card transporter-services-board">
              <div className="records-card__header">
                <div><span>SERVICE CATALOGUE</span><h3>Services sellers and businesses can choose</h3></div>
                <button type="button" onClick={() => setServiceModal(true)}>Add service</button>
              </div>
              <div className="transport-services-market-grid transport-services-market-grid--portal">
                {myServices.length ? myServices.map((service) => <TransporterServiceCard key={service.id} service={service} compact />) : <div className="records-empty soft-empty">No services yet. Add routes, pricing notes and an image so sellers can choose your transport service.</div>}
              </div>
            </section>

            <section className="transporter-job-columns">
              <article className="workspace-card transporter-queue-panel">
                <div className="workspace-section-title"><span>NEW DEALS</span><h2>Requests needing response</h2></div>
                <div className="delivery-deal-list">
                  {newRequests.length ? newRequests.map((item) => <JobCard key={item.id} item={item} mode="new" />) : <div className="records-empty soft-empty">New seller/business transport requests will appear here after your service is chosen for an order.</div>}
                </div>
              </article>
              <article className="workspace-card transporter-queue-panel">
                <div className="workspace-section-title"><span>DELIVERY PIPELINE</span><h2>Quoted and active jobs</h2></div>
                <div className="delivery-deal-list">
                  {activeJobs.length ? activeJobs.map((item) => <JobCard key={item.id} item={item} mode="active" />) : <div className="records-empty soft-empty">Quoted jobs appear here while payment is pending, followed by pickup, transit and delivery.</div>}
                </div>
              </article>
            </section>

            {completeJobs.length ? <section className="workspace-card transporter-queue-panel"><div className="workspace-section-title"><span>HISTORY</span><h2>Completed / cancelled deliveries</h2></div><div className="delivery-history-list">{completeJobs.slice(0, 6).map((item) => <div key={item.id}><strong>{item.code} • {item.order_number}</strong><span className={`status-chip ${statusTone(item.status)}`}>{formatStatus(item.status)}</span></div>)}</div></section> : null}
          </>
        )}
      </main>

      {profileModal && <Modal title={myCourier ? "Edit transporter profile" : "Create transporter profile"} eyebrow="TRANSPORTER SETUP" onClose={() => setProfileModal(false)}><form className="modal-form-grid polished-form-grid" onSubmit={saveProfile}>
        <div><label>Transporter name</label><input value={profileForm.transporter_name} onChange={(e) => setProfileForm((p) => ({ ...p, transporter_name: e.target.value }))} required /></div>
        <div><label>Vehicle type</label><select value={profileForm.vehicle_type} onChange={(e) => setProfileForm((p) => ({ ...p, vehicle_type: e.target.value }))}><option value="MOTORBIKE">Motorbike</option><option value="CAR">Car</option><option value="VAN">Van</option><option value="TRUCK">Truck</option><option value="BUS">Bus</option><option value="OTHER">Other</option></select></div>
        <div><label>Vehicle registration</label><input value={profileForm.vehicle_registration} onChange={(e) => setProfileForm((p) => ({ ...p, vehicle_registration: e.target.value }))} /></div>
        <div><label>Operational phone (private to TAZY CART)</label><input value={profileForm.contact_phone} onChange={(e) => setProfileForm((p) => ({ ...p, contact_phone: e.target.value }))} /></div>
        <div><label>Base city</label><input value={profileForm.base_city} onChange={(e) => setProfileForm((p) => ({ ...p, base_city: e.target.value }))} /></div>
        <div><label>Base province</label><input value={profileForm.base_province} onChange={(e) => setProfileForm((p) => ({ ...p, base_province: e.target.value }))} /></div>
        <div className="full"><label>Service area summary</label><textarea value={profileForm.service_area_summary} onChange={(e) => setProfileForm((p) => ({ ...p, service_area_summary: e.target.value }))} placeholder="Example: Victoria Falls, Hwange, Bulawayo and Harare intercity deliveries." /></div>
        <button type="submit">Save transporter profile</button>
      </form></Modal>}

      {docModal && <Modal title="Upload verification document" eyebrow="TRANSPORTER DOCUMENTS" onClose={() => setDocModal(false)}><form className="modal-form-grid polished-form-grid" onSubmit={uploadDocument}>
        <div><label>Document type</label><select value={docForm.document_type} onChange={(e) => { const label = documentTypes.find(([value]) => value === e.target.value)?.[1] || e.target.value; setDocForm((p) => ({ ...p, document_type: e.target.value, title: label })); }}>{documentTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
        <div><label>Document number</label><input value={docForm.document_number} onChange={(e) => setDocForm((p) => ({ ...p, document_number: e.target.value }))} /></div>
        <div className="full"><label>Attached file</label><input type="file" onChange={(e) => setDocForm((p) => ({ ...p, file: e.target.files?.[0] || null }))} required /></div>
        <button type="submit">Submit document for review</button>
      </form></Modal>}

      {serviceModal && <Modal title="Add delivery service" eyebrow="TRANSPORT SERVICE" onClose={() => setServiceModal(false)}><form className="modal-form-grid polished-form-grid" onSubmit={saveService}>
        {isAdmin && <div className="full"><label>Transporter</label><select value={serviceForm.courier || ""} onChange={(e) => setServiceForm((p) => ({ ...p, courier: e.target.value }))}><option value="">Select transporter</option>{couriers.map((item) => <option key={item.id} value={item.id}>{item.transporter_name}</option>)}</select></div>}
        <div><label>Service name</label><input value={serviceForm.service_name} onChange={(e) => setServiceForm((p) => ({ ...p, service_name: e.target.value }))} placeholder="Same day Victoria Falls" required /></div>
        <div><label>Service type</label><select value={serviceForm.service_type} onChange={(e) => setServiceForm((p) => ({ ...p, service_type: e.target.value }))}>{serviceTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
        <div><label>Base price</label><input value={serviceForm.base_price} onChange={(e) => setServiceForm((p) => ({ ...p, base_price: e.target.value }))} /></div>
        <div><label>Service areas</label><input value={serviceForm.service_areas_text} onChange={(e) => setServiceForm((p) => ({ ...p, service_areas_text: e.target.value }))} placeholder="Harare, Bulawayo, Vic Falls" /></div>
        <div className="full"><label>Service image</label><input type="file" accept="image/*" onChange={(e) => setServiceForm((p) => ({ ...p, service_image: e.target.files?.[0] || null }))} /></div>
        <div className="full"><label>Price notes</label><textarea value={serviceForm.price_notes} onChange={(e) => setServiceForm((p) => ({ ...p, price_notes: e.target.value }))} placeholder="Pricing depends on weight, distance or item type." /></div>
        <button type="submit">Save delivery service</button>
      </form></Modal>}

      {statusModal && <Modal title="Send delivery quote" eyebrow="DELIVERY DEAL" onClose={() => setStatusModal(null)}><form className="modal-form-grid polished-form-grid" onSubmit={saveDeliveryStatus}>
        <div><label>Quoted amount</label><input type="number" min="0.01" step="0.01" required value={statusForm.quoted_amount} onChange={(e) => setStatusForm((p) => ({ ...p, quoted_amount: e.target.value }))} placeholder="Delivery service charge" /></div>
        <div><label>Scheduled date</label><input type="date" value={statusForm.scheduled_date || ""} onChange={(e) => setStatusForm((p) => ({ ...p, scheduled_date: e.target.value }))} /></div>
        <p className="modal-note full">The seller will see this quote and pay the delivery charge. The job becomes assigned only after payment is confirmed.</p>
        <button type="submit">Send quote</button>
      </form></Modal>}
    </div>
  );
}
